import { useEffect, useState } from 'react';

/**
 * Detects whether the current device is "mobile" for the purpose of the
 * business-card scan flow (Evolution 2bis).
 *
 * Two signals are combined so that:
 *   1) iPad / desktop touch screens that *can* scan are not falsely excluded
 *   2) Resized desktop windows do not switch into "scan mode" by accident
 *
 * Rule: mobile == viewport < 768px AND either userAgent says mobile, or
 * the device exposes a camera-capable touch surface (coarse pointer + touch).
 *
 * The hook is SSR-safe and updates on resize / orientation change.
 */
export function useIsMobileDevice(): boolean {
  const [isMobile, setIsMobile] = useState(() => detectMobile());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(detectMobile());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return isMobile;
}

export function detectMobile(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const narrowViewport = window.innerWidth < 768;
  const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent ?? '',
  );
  const coarseTouch =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches &&
    'ontouchstart' in window;
  return narrowViewport && (uaMobile || coarseTouch);
}
